import React, { useState } from "react";
import { Container, Typography, Button, Box } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { AddIcon } from "../../customIcons";
import { styles } from "../../styles/dataTableMD/dataTableHeader";
import { useTheme } from "@mui/material/styles";
import AddModal from "../modal/modal";
import { api } from "../../../../api";
import { getTitleString } from "../../../../utils/dataUtils";

// CSV-safe cell: arrays -> "; " joined; objects -> JSON; everything else -> string. Quote when the
// value contains a comma, quote, or newline (doubling embedded quotes) per RFC 4180.
const formatCell = (v) => {
  if (v === null || v === undefined) return "";
  let s;
  if (Array.isArray(v)) s = v.join("; ");
  else if (typeof v === "object") s = JSON.stringify(v);
  else s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const DataGridHeader = ({
  title,
  dbTable,
  toggles,
  processFilter,
  addButton,
  columns,
  addSuccessMessage,
  allData,
  setAllData,
  isTab,
  exportRows,
}) => {
  const user = JSON.parse(localStorage.getItem("user"));

  // Export the current (filtered + sorted) rows to a CSV download. Uses the configured columns
  // for headers + field order; falls back to the loaded rows if exportRows isn't provided.
  const handleExport = () => {
    const cols = (columns || []).filter((c) => c && c.field && c.showColumn !== false);
    const rows = Array.isArray(exportRows) && exportRows.length ? exportRows : allData || [];
    const headerLine = cols.map((c) => formatCell(c.headerName || c.field)).join(",");
    const bodyLines = rows.map((row) => cols.map((c) => formatCell(row[c.field])).join(","));
    const csv = [headerLine, ...bodyLines].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${String(title || "export").toLowerCase().replace(/\s+/g, "-")}-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const getInitValues = () => {
    const initValues = {};
    columns.forEach((column) => {
      if (column.showAdd) {
        if (column.field === "district" && user.permission !== "disclosureAdmin") {
          initValues[column.field] = user.district;
        } else if (column.field === "school" && user.permission !== "disclosureAdmin" && user.permission !== "districtAdmin") {
          initValues[column.field] = user.school;
        } else if (column.addDefaultValue) {
          initValues[column.field] = column.addDefaultValue;
        } else {
          initValues[column.field] = "";
        }
      }
    });
    return initValues;
  };
  const theme = useTheme();
  const style = styles(theme);

  const [submit, setSubmit] = useState(false);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(getInitValues());
  const [addError, setAddError] = useState(null);

  const handleOpen = () => {
    setOpen(true);
    setAddError(null);
    setValues(getInitValues());
  };

  const handleClose = () => {
    setOpen(false);
    setSubmit(false);
    setAddError(null);
  };

  const handleSubmit = async (values, setAdding) => {
    if (user.permission !== "disclosureAdmin") {
      values.district = user.district;
    }
    if (user.permission !== "disclosureAdmin" && user.permission !== "districtAdmin") {
      values.school = user.school;
    }
    setAdding(true);
    setAddError(null);
    for (let value in values) {
      if (values[value] === "") {
        delete values[value];
      }
    }
    const results = await api("post", values, dbTable);
    if (results.status === 200) {
      setSubmit(true);
      setAdding(false);
      setAllData([...allData, results.data]);
    } else {
      // BUG-003: surface the backend's specific, actionable message instead of failing silently.
      const message =
        results?.data?.message ||
        results?.data?.error ||
        "Something went wrong creating this record. Please try again.";
      setAddError(message);
      setAdding(false);
    }
  };

  const handleToggleClick = async (toggleOption) => {
    await processFilter(toggles.field, [toggleOption]);
  };

  return (
    <Container
      sx={
        addButton
          ? isTab
            ? style.DataGridHeaderWithAddButton
            : style.DataGridHeaderWithOutAddButton
          : style.DataGridHeaderContainer
      }
    >
      {!isTab && (
        <Typography sx={style.DataGridHeaderTitle} variant="h4">
          {title}
        </Typography>
      )}
      <Box>
        {addButton && (
          <Button
            sx={{
              color: theme.palette.common.white,
            }}
            size="medium"
            variant="contained"
            onClick={() => handleOpen()}
            endIcon={<AddIcon />}
          >
            {`Add A New ${getTitleString(title)}`}
          </Button>
        )}

        {toggles &&
          toggles.options.map((toggleOption, index) => (
            <Button
              sx={[
                style.DataGridHeaderButton,
                toggles.active && toggles.active.includes(toggleOption)
                  ? style.DataGridHeaderButtonActive
                  : style.DataGridHeaderButtonInactive,
              ]}
              size="medium"
              variant="contained"
              key={index}
              onClick={(e) => handleToggleClick(toggleOption)}
            >
              {toggleOption}
            </Button>
          ))}

        <Button
          sx={[style.DataGridHeaderButton, { color: theme.palette.primary.main }]}
          size="medium"
          variant="outlined"
          onClick={handleExport}
          endIcon={<FileDownloadOutlinedIcon />}
        >
          Export
        </Button>
      </Box>
      {addButton && (
        <AddModal
          open={open}
          submit={submit}
          handleClose={handleClose}
          title={getTitleString(title)}
          handleSubmit={handleSubmit}
          columns={columns}
          addSuccessMessage={addSuccessMessage}
          values={values}
          setValues={setValues}
          error={addError}
        />
      )}
    </Container>
  );
};

export default DataGridHeader;
